import React, { useState, useEffect } from 'react';
import {
    Input,
    Select,
    Switch,
    Button,
    Space,
    Row,
    Col,
    Divider,
    Typography,
    Card,
    Upload,
    Image
} from 'antd';
import {
    EditOutlined,
    UserOutlined,
    PhoneOutlined,
    MailOutlined,
    HomeOutlined,
    SaveOutlined,
    CloseOutlined,
    UploadOutlined,
    DeleteOutlined,
    GlobalOutlined,
    SafetyOutlined,
    BankOutlined,
    CalendarOutlined
} from '@ant-design/icons';
import { API_URL, LOGGED_USER, TOKEN } from '../../../variables/constants';
import axiosMultiPartDataInstance from '../../../variables/api/axiosMultiPartDatainstance';
import { toast } from 'react-toastify';
import SDIModal from '../../../components/ui/SDIModal';
import { getCompagnieLogo } from '../../../hooks/useFilesHooks';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Palette de couleurs
const colors = {
    primary: '#242166',
    secondary: '#1890ff',
    success: '#52c41a',
    warning: '#faad14',
    danger: '#ff4d4f',
    light: '#f0f2f5',
    gradient1: 'linear-gradient(135deg, #242166 0%, #1890ff 100%)',
    gradient2: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
};

const EditMembreModal = ({
    visible,
    onCancel,
    onSubmit,
    membreData,
    exercices = []
}) => {
    const isAdmin = (LOGGED_USER?.profil?.nom || '').toLowerCase().includes('admin');
    const [raisonSociale, setRaisonSociale] = useState('');
    const [email, setEmail] = useState('');
    const [siegeSocial, setSiegeSocial] = useState('');
    const [dureeExpiration, setDureeExpiration] = useState(0);
    const [code, setCode] = useState('');
    const [typeMembre, setTypeMembre] = useState('ASSURANCE');
    const [dateCreation, setDateCreation] = useState('');
    const [defaultExercice, setDefaultExercice] = useState(new Date().getFullYear());
    const [telephone1, setTelephone1] = useState('');
    const [telephone2, setTelephone2] = useState('');
    const [responsable, setResponsable] = useState('');
    const [otpConnexion, setOtpConnexion] = useState('NONE');
    const [domaine, setDomaine] = useState('');
    const [website, setWebsite] = useState('');
    const [emailProduction, setEmailProduction] = useState('');
    const [emailSinistre, setEmailSinistre] = useState('');
    const [active, setActive] = useState(true);
    const [loading, setLoading] = useState(false);

    // Gestion des images
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [headerPreviewUrl, setHeaderPreviewUrl] = useState(null);
    const [footerPreviewUrl, setFooterPreviewUrl] = useState(null);
    const [fileImage, setFileImage] = useState(null);
    const [headerImage, setHeaderImage] = useState(null);
    const [footerImage, setFooterImage] = useState(null);
    const [changeLogo, setChangeLogo] = useState(false);
    const [changeHeader, setChangeHeader] = useState(false);
    const [changeFooter, setChangeFooter] = useState(false);

    const [errors, setErrors] = useState({});

    // Charger les données du membre
    useEffect(() => {
        if (membreData && visible) {
            setRaisonSociale(membreData.raisonSociale || '');
            setEmail(membreData.email || '');
            setSiegeSocial(membreData.siegeSocial || '');
            setCode(membreData.code || '');
            setTypeMembre(membreData.typeOrganisme || 'ASSURANCE');
            setDateCreation(membreData.dateCreation || '');
            setDefaultExercice(membreData.defaultExercice?.exerciceId || new Date().getFullYear());
            setResponsable(membreData.responsable || '');
            setOtpConnexion(membreData.otpConnexion || 'NONE');
            setDomaine(membreData.domaine || '');
            setWebsite(membreData.website || '');
            setEmailProduction(membreData.emailProduction || '');
            setEmailSinistre(membreData.emailSinistre || '');
            setActive(membreData.active || true);
            setDureeExpiration(membreData.dureeExpiration || 0);

            // Gestion des contacts
            setTelephone1(membreData?.telephone1 || membreData.contact && membreData.contact[0] || '');
            setTelephone2(membreData?.telephone2 || membreData.contact && membreData.contact[1] || '');


            // Images (à adapter selon votre API)
            if (membreData?.logo) {
                getCompagnieLogo(membreData?.code)
                    .then(async (response) => {
                        const imageBytes = await response.arrayBuffer();
                        var blob = new Blob([imageBytes], { type: "image/png" });
                        var imageUrl = URL.createObjectURL(blob);
                        setImagePreviewUrl(imageUrl);
                        // Si besoin, on peut reconstruire un File object, mais pas nécessaire pour l'affichage
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            } else {
                setImagePreviewUrl(null);
            }

            if (membreData?.entete) {
                fetch(`${API_URL}/api/organisme/logo/${membreData?.entete.split("/")[1]}`, {
                    headers: {
                        Authorization: `Bearer ${TOKEN}`,
                        "Content-Type": "application/json",
                    },
                })
                    .then(async (response) => {
                        const imageBytes = await response.arrayBuffer();
                        var blob = new Blob([imageBytes], { type: "image/png" });
                        var imageUrl = URL.createObjectURL(blob);
                        setHeaderPreviewUrl(imageUrl);
                        // Si besoin, on peut reconstruire un File object, mais pas necesario pour l'affichage
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            } else {
                setHeaderPreviewUrl(null);
            }
            if (membreData?.footer) {
                fetch(`${API_URL}/api/organisme/logo/${membreData?.footer.split("/")[1]}`, {
                    headers: {
                        Authorization: `Bearer ${TOKEN}`,
                        "Content-Type": "application/json",
                    },
                })
                    .then(async (response) => {
                        const imageBytes = await response.arrayBuffer();
                        var blob = new Blob([imageBytes], { type: "image/png" });
                        var imageUrl = URL.createObjectURL(blob);
                        setFooterPreviewUrl(imageUrl);
                        // Si besoin, on peut reconstruire un File object, mais pas necesario para l'affichage
                    })
                    .catch((error) => {
                        console.log(error);
                    });
            } else {
                setFooterPreviewUrl(null);
            }
        }
    }, [membreData, visible]);

    const validateForm = () => {
        const newErrors = {};

        if (!raisonSociale || raisonSociale.length < 3) {
            newErrors.raisonSociale = 'La dénomination sociale est requise';
        }

        if (!email) {
            newErrors.email = 'L\'email est requis';
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newErrors.email = 'Format d\'email invalide';
        }

        if (!telephone1) {
            newErrors.telephone1 = 'Le téléphone est requis';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        setLoading(true);
        if (!validateForm()) {
            return;
        }

        const formData = new FormData();

        const contacts = telephone2 ? [telephone1, telephone2] : [telephone1];
        const myExercice = exercices.find(ex => ex.exerciceId === parseInt(defaultExercice));

        const organisme = {
            organismeId: membreData?.organismeId,
            typeOrganisme: typeMembre,
            raisonSociale,
            code,
            contact: contacts,
            telephone1, telephone2,
            email,
            responsable,
            siegeSocial,
            active,
            defaultExercice: myExercice,
            otpConnexion,
            domaine,
            emailProduction,
            emailSinistre,
            website,
            dureeExpiration
        };

        // Gestion des fichiers images
        if (changeLogo && fileImage) {
            formData.append('fichier', fileImage);
        }
        if (changeHeader && headerImage) {
            formData.append('header', headerImage);
        }
        if (changeFooter && footerImage) {
            formData.append('footer', footerImage);
        }

        formData.append('organisme', JSON.stringify(organisme));
        formData.append('dateCreation', dateCreation);

        axiosMultiPartDataInstance
            .put(`/api/organisme/with-logos`, formData)
            .then((response) => {
                console.log("response", response);
                setLoading(false);
                toast("Membre mis à jour avec succès!");
                window.location.reload();
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
            });
    };

    const handleCancel = () => {
        setErrors({});
        setChangeLogo(false);
        setChangeHeader(false);
        setChangeFooter(false);
        onCancel();
    };

    // Gestion upload logo
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFileImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
            setChangeLogo(true);
        }
    };

    // Gestion upload header
    const handleHeaderChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setHeaderImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setHeaderPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
            setChangeHeader(true);
        }
    };

    // Gestion upload footer
    const handleFooterChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFooterImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setFooterPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
            setChangeFooter(true);
        }
    };

    const resetImage = () => {
        setImagePreviewUrl(null);
        setChangeLogo(false);
        setFileImage(null);
    };

    const resetHeader = () => {
        setHeaderPreviewUrl(null);
        setChangeHeader(false);
        setHeaderImage(null);
    };

    const resetFooter = () => {
        setFooterPreviewUrl(null);
        setChangeFooter(false);
        setFooterImage(null);
    };

    return (
        <SDIModal
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={1000}
            centered
            style={{ top: 20 }}
            bodyStyle={{ padding: 0 }}
            closeIcon={
                <CloseOutlined style={{ color: 'white', fontSize: '18px' }} />
            }
        >
            {/* Header avec dégradé */}
            <div
                style={{
                    background: colors.gradient1,
                    padding: '32px 40px',
                    borderRadius: '8px 8px 0 0'
                }}
            >
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    <EditOutlined style={{ fontSize: '48px', color: 'white' }} />
                    <Title level={3} style={{ color: 'white', margin: 0 }}>
                        Modifier les informations du membre
                    </Title>
                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px' }}>
                        Mettez à jour les informations de <span style={{ fontWeight: 'bold', color: 'white' }}>{membreData?.raisonSociale}</span>
                    </Text>
                </Space>
            </div>

            {/* Contenu du formulaire */}
            <div style={{ padding: '32px 40px', maxHeight: '75vh', overflowY: 'auto' }}>

                {/* Section Informations principales */}
                <Card
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        border: `1px solid ${colors.light}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                >
                    <Space direction="vertical" size={4} style={{ marginBottom: '20px', width: '100%' }}>
                        <Text style={{ fontSize: '16px', fontWeight: 600, color: colors.primary }}>
                            <BankOutlined style={{ marginRight: '8px' }} />
                            Informations générales
                        </Text>
                    </Space>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>
                                        Dénomination sociale <span style={{ color: colors.danger }}>*</span>
                                    </span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="Dénomination sociale"
                                    value={raisonSociale}
                                    onChange={(e) => setRaisonSociale(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                    status={errors.raisonSociale ? 'error' : ''}
                                />
                                {errors.raisonSociale && (
                                    <Text type="danger" style={{ fontSize: '12px' }}>{errors.raisonSociale}</Text>
                                )}
                            </div>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>
                                        Adresse email <span style={{ color: colors.danger }}>*</span>
                                    </span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="email@exemple.com"
                                    prefix={<MailOutlined style={{ color: colors.primary }} />}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                    status={errors.email ? 'error' : ''}
                                />
                                {errors.email && (
                                    <Text type="danger" style={{ fontSize: '12px' }}>{errors.email}</Text>
                                )}
                            </div>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Siège social</span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="Adresse du siège"
                                    prefix={<HomeOutlined style={{ color: colors.primary }} />}
                                    value={siegeSocial}
                                    onChange={(e) => setSiegeSocial(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Durée expiration mot de passe (jours)</span>
                                </div>
                                <Input
                                    size="large"
                                    type="number"
                                    placeholder="90"
                                    value={dureeExpiration}
                                    onChange={(e) => setDureeExpiration(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={6}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Code membre</span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="Code"
                                    value={code}
                                    onChange={(e) => setCode(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                    disabled
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={6}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Type membre</span>
                                </div>
                                <Select
                                    size="large"
                                    value={typeMembre}
                                    onChange={setTypeMembre}
                                    style={{ width: '100%', borderRadius: '8px' }}
                                    disabled={LOGGED_USER?.organisme?.typeOrganisme === 'ASSURANCE'}
                                >
                                    <Option value="ASSURANCE">ASSURANCE</Option>
                                    <Option value="PARTENAIRE">PARTENAIRE</Option>
                                </Select>
                            </div>
                        </Col>

                        <Col xs={24} md={6}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Date de création</span>
                                </div>
                                <Input
                                    size="large"
                                    type="date"
                                    value={dateCreation}
                                    onChange={(e) => setDateCreation(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                    prefix={<CalendarOutlined style={{ color: colors.primary }} />}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={6}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Exercice par défaut</span>
                                </div>
                                <Select
                                    size="large"
                                    value={defaultExercice}
                                    onChange={setDefaultExercice}
                                    style={{ width: '100%', borderRadius: '8px' }}
                                >
                                    {exercices.map((exo, id) => (
                                        <Option key={id} value={exo.exerciceId}>
                                            {exo.libelleExercice}
                                        </Option>
                                    ))}
                                </Select>
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Section Contacts */}
                <Card
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        border: `1px solid ${colors.light}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                >
                    <Space direction="vertical" size={4} style={{ marginBottom: '20px', width: '100%' }}>
                        <Text style={{ fontSize: '16px', fontWeight: 600, color: colors.primary }}>
                            <PhoneOutlined style={{ marginRight: '8px' }} />
                            Contacts et responsable
                        </Text>
                    </Space>

                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Responsable</span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="Nom du responsable"
                                    prefix={<UserOutlined style={{ color: colors.primary }} />}
                                    value={responsable}
                                    onChange={(e) => setResponsable(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={8}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>
                                        Téléphone 1 <span style={{ color: colors.danger }}>*</span>
                                    </span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="90 12 34 56"
                                    prefix={<PhoneOutlined style={{ color: colors.primary }} />}
                                    addonBefore="TG (+228)"
                                    value={telephone1}
                                    onChange={(e) => setTelephone1(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                    maxLength={8}
                                    status={errors.telephone1 ? 'error' : ''}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={8}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Téléphone 2</span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="91 23 45 67"
                                    prefix={<PhoneOutlined style={{ color: colors.primary }} />}
                                    addonBefore="TG (+228)"
                                    value={telephone2}
                                    onChange={(e) => setTelephone2(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                    maxLength={8}
                                />
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Section Paramètres avancés */}
                <Card
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        border: `1px solid ${colors.light}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                >
                    <Space direction="vertical" size={4} style={{ marginBottom: '20px', width: '100%' }}>
                        <Text style={{ fontSize: '16px', fontWeight: 600, color: colors.primary }}>
                            <SafetyOutlined style={{ marginRight: '8px' }} />
                            Paramètres avancés
                        </Text>
                    </Space>

                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Double authentification</span>
                                </div>
                                <Select
                                    size="large"
                                    value={otpConnexion}
                                    onChange={setOtpConnexion}
                                    style={{ width: '100%', borderRadius: '8px' }}
                                >
                                    <Option value="NONE">Désactiver</Option>
                                    <Option value="SMS">SMS</Option>
                                    <Option value="EMAIL">EMAIL</Option>
                                    <Option value="WHATSAPP">WHATSAPP</Option>
                                </Select>
                            </div>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Nom de domaine</span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="exemple.tg"
                                    prefix={<GlobalOutlined style={{ color: colors.primary }} />}
                                    value={domaine}
                                    onChange={(e) => setDomaine(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Site web</span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="www.exemple.tg"
                                    prefix={<GlobalOutlined style={{ color: colors.primary }} />}
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Email production</span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="production@exemple.tg"
                                    prefix={<MailOutlined style={{ color: colors.primary }} />}
                                    value={emailProduction}
                                    onChange={(e) => setEmailProduction(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                />
                            </div>
                        </Col>

                        <Col xs={24} md={12}>
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                    <span style={{ fontWeight: 600 }}>Email sinistres</span>
                                </div>
                                <Input
                                    size="large"
                                    placeholder="sinistres@exemple.tg"
                                    prefix={<MailOutlined style={{ color: colors.primary }} />}
                                    value={emailSinistre}
                                    onChange={(e) => setEmailSinistre(e.target.value)}
                                    style={{ borderRadius: '8px' }}
                                />
                            </div>
                        </Col>
                    </Row>
                </Card>

                {/* Section Images */}
                <Card
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        border: `1px solid ${colors.light}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                >
                    <Space direction="vertical" size={4} style={{ marginBottom: '20px', width: '100%' }}>
                        <Text style={{ fontSize: '16px', fontWeight: 600, color: colors.primary }}>
                            <UploadOutlined style={{ marginRight: '8px' }} />
                            Logos et images
                        </Text>
                    </Space>

                    {/* Logo */}
                    <div style={{ marginBottom: '24px' }}>
                        <Text strong style={{ display: 'block', marginBottom: '12px' }}>Logo de l'organisme</Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            {imagePreviewUrl && (
                                <img
                                    src={imagePreviewUrl}
                                    alt="Logo"
                                    style={{
                                        width: '120px',
                                        height: '80px',
                                        objectFit: 'contain',
                                        border: `1px solid ${colors.light}`,
                                        borderRadius: '8px',
                                        padding: '8px'
                                    }}
                                />
                            )}
                            <Space>
                                <label htmlFor="logo-upload">
                                    <Button
                                        size="large"
                                        icon={<UploadOutlined />}
                                        style={{ borderRadius: '8px' }}
                                        onClick={() => document.getElementById('logo-upload').click()}
                                    >
                                        Téléverser
                                    </Button>
                                    <input
                                        id="logo-upload"
                                        type="file"
                                        accept="image/png, image/jpeg"
                                        onChange={handleImageChange}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                                {imagePreviewUrl && (
                                    <Button
                                        size="large"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={resetImage}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        Supprimer
                                    </Button>
                                )}
                            </Space>
                        </div>
                    </div>

                    {/* Header */}
                    <div style={{ marginBottom: '24px' }}>
                        <Text strong style={{ display: 'block', marginBottom: '12px' }}>En-tête de document</Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            {headerPreviewUrl && (
                                <img
                                    src={headerPreviewUrl}
                                    alt="Header"
                                    style={{
                                        width: '200px',
                                        height: '60px',
                                        objectFit: 'contain',
                                        border: `1px solid ${colors.light}`,
                                        borderRadius: '8px',
                                        padding: '8px'
                                    }}
                                />
                            )}
                            <Space>
                                <label htmlFor="header-upload">
                                    <Button
                                        size="large"
                                        icon={<UploadOutlined />}
                                        style={{ borderRadius: '8px' }}
                                        onClick={() => document.getElementById('header-upload').click()}
                                    >
                                        Téléverser
                                    </Button>
                                    <input
                                        id="header-upload"
                                        type="file"
                                        accept="image/png, image/jpeg"
                                        onChange={handleHeaderChange}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                                {headerPreviewUrl && (
                                    <Button
                                        size="large"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={resetHeader}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        Supprimer
                                    </Button>
                                )}
                            </Space>
                        </div>
                    </div>

                    {/* Footer */}
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: '12px' }}>Pied de page de document</Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            {footerPreviewUrl && (
                                <img
                                    src={footerPreviewUrl}
                                    alt="Footer"
                                    style={{
                                        width: '200px',
                                        height: '60px',
                                        objectFit: 'contain',
                                        border: `1px solid ${colors.light}`,
                                        borderRadius: '8px',
                                        padding: '8px'
                                    }}
                                />
                            )}
                            <Space>
                                <label htmlFor="footer-upload">
                                    <Button
                                        size="large"
                                        icon={<UploadOutlined />}
                                        style={{ borderRadius: '8px' }}
                                        onClick={() => document.getElementById('footer-upload').click()}
                                    >
                                        Téléverser
                                    </Button>
                                    <input
                                        id="footer-upload"
                                        type="file"
                                        accept="image/png, image/jpeg"
                                        onChange={handleFooterChange}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                                {footerPreviewUrl && (
                                    <Button
                                        size="large"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={resetFooter}
                                        style={{ borderRadius: '8px' }}
                                    >
                                        Supprimer
                                    </Button>
                                )}
                            </Space>
                        </div>
                        <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                            JPG, GIF ou PNG autorisés. Taille maximale de 2 Mo
                        </Text>
                    </div>
                </Card>

                {/* Section Statut */}
                <Card
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        border: `1px solid ${colors.light}`,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        background: active ? 'rgba(82, 196, 26, 0.05)' : 'rgba(255, 77, 79, 0.05)'
                    }}
                    bodyStyle={{ padding: '24px' }}
                >
                    <Row align="middle" justify="space-between">
                        <Col>
                            <Space direction="vertical" size={4}>
                                <Text style={{ fontSize: '16px', fontWeight: 600, color: colors.primary }}>
                                    Statut du membre
                                </Text>
                                <Text type="secondary" style={{ fontSize: '13px' }}>
                                    {active ? 'Actif' : 'Inactif'}
                                </Text>
                            </Space>
                        </Col>
                        <Col>
                            <Switch
                                checked={active}
                                onChange={setActive}
                                checkedChildren="Actif"
                                unCheckedChildren="Inactif"
                                disabled={isAdmin}
                                style={{
                                    backgroundColor: active ? colors.success : colors.danger,
                                    borderRadius: '20px'
                                }}
                            />
                        </Col>
                    </Row>
                </Card>

                {/* Boutons d'action */}
                <div style={{ textAlign: 'right' }}>
                    <Space>
                        <Button
                            size="large"
                            onClick={handleCancel}
                            icon={<CloseOutlined />}
                            style={{ borderRadius: '8px' }}
                        >
                            Annuler
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            onClick={handleSubmit}
                            icon={<SaveOutlined />}
                            loading={loading}
                            style={{ borderRadius: '8px', backgroundColor: colors.primary, borderColor: colors.primary }}
                        >
                            Enregistrer les modifications
                        </Button>
                    </Space>
                </div>
            </div>
        </SDIModal>
    );
}

export default EditMembreModal;
